import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../../app/useAolSaveHandler';
import { FuelRobDataTypeEnum, FuelTypeEnum, NoonReportSectionEnum } from '../../../../types/ViewModel/AolEnums';
import { FuelRobPerTank } from '../../../../types/ViewModel/FuelRobPerTank';
import { NoonReportSectionData } from '../../../../types/ViewModel/NoonReportSectionData';
import { getFuelTypeString, getPreviousRobValue, getRobValue, hasInvalidFields, hasMissingValues } from '../../../../utils/aolFieldsHelper';
import { ErrorText } from '../../../shared/error-text/ErrorText.style';
import { ListHeaderStyle, SectionInnerWrapper, SubSectionHeader } from '../../aol-container/AolContainer.style';
import OdfjellInputComponent, { InputChangeData } from '../../../common/odfjell-input-component/OdfjellInputComponent';
import { GridWrapper, TankWrapper } from '../FuelRobSection.style';
import { roundTo2Decimals, truncateTo2Decimals } from '../../../../utils/numberHelper';
import { useAolStore } from '../../../../context/AolContext';

type Props = {
    vm: any;
    fuelRobVM: any;
    fuelRobPerTanks: FuelRobPerTank[];
    fuelType: FuelTypeEnum;
    unit: string;
    fuelRobDataType: FuelRobDataTypeEnum;
    portName: string;
    hasEmptyValues;
    sectionData?: NoonReportSectionData;
    isReadOnly: boolean;
};

const FuelRobPerTypeComponent: React.FC<Props> = (props) => {
    const [headerString, setHeaderString] = useState('');
    const [convertedFuelRobPerTanks, setConvertedFuelRobPerTanks] = useState<FuelRobPerTank[]>([]);
    const [hasEmptyValuesLocal, setHasEmptyValuesLocal] = useState(false);
    const { saveAolEvent } = useAolSaveHandler();
    const { selectedAolDataItem, selectedAolGroup } = useAolStore();

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.FuelRob &&
            selectedAolDataItem &&
            saveAolEvent.aolDataItem.id === selectedAolDataItem.id
        ) {
            if (saveAolEvent.type === 'save_section') checkOnSave();
            if (saveAolEvent.type === 'save_section_done') initVM();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        if (!props) return;
        initVM();
    }, [props]);

    const initVM = () => {
        setConvertedFuelRobPerTanks([...props.fuelRobPerTanks]);
        setHeaderString(getHeader());
        setHasEmptyValuesLocal(false);
    };

    const getHeader = () => {
        if (props.fuelType === FuelTypeEnum.HFO) return 'HFO est. at noon';
        if (props.fuelType === FuelTypeEnum.MGO) return 'MGO est. at noon';

        return '';
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;
        changeObject.dataObject[name] = value ? +value : undefined;

        hasInvalidFields(props.vm, name, changeObject.isValid);
        if (hasEmptyValuesLocal) setHasEmptyValuesLocal(false);
        setConvertedFuelRobPerTanks([...props.fuelRobPerTanks]);
    };

    const checkOnSave = () => {
        const ignoreList: string[] = ['bunkeringInMt', 'litreWeight', 'sulphurPercent'];
        const hasMissing = props.fuelRobPerTanks.some((rob) => {
            return hasMissingValues(rob, ignoreList);
        });

        if (hasMissing) {
            setHasEmptyValuesLocal(true);
            props.hasEmptyValues.current = true;
        } else {
            if (hasEmptyValuesLocal) setHasEmptyValuesLocal(false);
            props.hasEmptyValues.current = false;
        }
    };

    const getMaxTankWeight = (fuelRobPerTank: FuelRobPerTank): number => {
        return truncateTo2Decimals(
            fuelRobPerTank !== undefined && fuelRobPerTank.tankVolume > 0 && fuelRobPerTank.litreWeight > 0
                ? fuelRobPerTank.tankVolume * fuelRobPerTank.litreWeight
                : 10000
        );
    };

    const getTotalConsumptionForGroup = () => {
        if (selectedAolGroup) {
            if (props.fuelType === FuelTypeEnum.HFO)
                return selectedAolGroup.aolDataItems.reduce((sum, a) => (sum += a.aolDataItemProperties.totalHfoConsumption), 0);
            if (props.fuelType === FuelTypeEnum.MGO)
                return selectedAolGroup.aolDataItems.reduce((sum, a) => (sum += a.aolDataItemProperties.totalMgoConsumption), 0);
        }

        return 0;
    };

    const getRobDifference = () => {
        const totalRobValue = getTotalRobValue();
        const totalPreviousRobValue = props.fuelRobPerTanks.reduce((a, b) => (a += getPreviousRobValue(b, props.unit)), 0);
        const totalConsumption = getTotalConsumptionForGroup();
        if (totalRobValue > 0 && totalPreviousRobValue > 0) {
            return totalPreviousRobValue - totalRobValue - totalConsumption;
        }
        return 0;
    };

    const getTotalRobValue = () => {
        return props.fuelRobPerTanks.reduce((a, b) => a + getRobValue(b, props.unit), 0);
    };

    const hasTotalRobValidationError = () => {
        if (props.fuelRobVM.isFirstRobSectionInAol) {
            const totalRobValue = getTotalRobValue();
            const difference = getRobDifference();
            return Math.abs(difference / totalRobValue) > 0.05;
        }
    };

    return (
        <>
            {props.fuelRobPerTanks && (
                <SectionInnerWrapper>
                    <SubSectionHeader className="flex-space-between">
                        <span>{headerString}</span>
                        <span>
                            {roundTo2Decimals(props.fuelRobPerTanks.reduce((a, b) => a + b.robInMt, 0))} {'MT'}
                        </span>
                    </SubSectionHeader>
                    <TankWrapper>
                        <GridWrapper gridCols={3}>
                            <ListHeaderStyle>
                                <li>
                                    <label>Tank</label>
                                    <span className="col-rob">Previous ROB</span>
                                    <span className="col-rob">ROB</span>
                                </li>
                            </ListHeaderStyle>
                        </GridWrapper>
                        <GridWrapper gridCols={3}>
                            {convertedFuelRobPerTanks.map((fuelRobPerTank) => (
                                <li key={fuelRobPerTank.tankId}>
                                    <span>{fuelRobPerTank.tankName} </span>
                                    <span className="col-rob">
                                        <OdfjellInputComponent
                                            isReadOnly={true}
                                            type={'number'}
                                            unit={'MT'}
                                            value={fuelRobPerTank.previousRobInMt}
                                            name={'previousRobInMt'}
                                            dataObject={fuelRobPerTank}
                                        />
                                    </span>
                                    <span className="col-rob">
                                        <OdfjellInputComponent
                                            isReadOnly={props.isReadOnly}
                                            type={'number'}
                                            unit={'MT'}
                                            value={fuelRobPerTank.robInMt}
                                            name={'robInMt'}
                                            min={0}
                                            max={getMaxTankWeight(fuelRobPerTank)}
                                            dataObject={fuelRobPerTank}
                                            onChange={onInputChange}
                                        />
                                    </span>
                                </li>
                            ))}
                        </GridWrapper>
                    </TankWrapper>
                    {hasEmptyValuesLocal && <ErrorText>You must enter all data before saving.</ErrorText>}
                    {hasTotalRobValidationError() && (
                        <ErrorText>
                            {getFuelTypeString(props.fuelType)} difference: {getRobDifference().toFixed(2)}
                            {props.unit}
                        </ErrorText>
                    )}
                </SectionInnerWrapper>
            )}
        </>
    );
};

export default FuelRobPerTypeComponent;
